import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaEdit, FaEye, FaSearch, FaSyncAlt, FaUserPlus, FaUsers } from 'react-icons/fa';
import DashboardLayout from '../components/DashboardLayout';
import CustomDataTable from '../components/CustomDataTable';
import { apiRequest } from '../utils/api';
import { getActiveCompanyId } from '../utils/companyContext';
import './EmployeeLoginList.css';

const DEFAULT_PAGE_SIZE = 25;

const EmployeeLoginList = () => {
  const navigate = useNavigate();
  const [logins, setLogins] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(DEFAULT_PAGE_SIZE);
  const [totalRows, setTotalRows] = useState(0);
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [editingLogin, setEditingLogin] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editUsername, setEditUsername] = useState('');
  const [editPassword, setEditPassword] = useState('');
  const [editStatus, setEditStatus] = useState('active');
  const [editEducationMandatory, setEditEducationMandatory] = useState(true);
  const [editError, setEditError] = useState('');
  const [editSaving, setEditSaving] = useState(false);
  const [activeCompanyId, setActiveCompanyId] = useState(() => getActiveCompanyId());

  const fetchLogins = useCallback(
    async (pageValue = page, perPageValue = perPage) => {
      try {
        setLoading(true);
        setError('');

        if (!activeCompanyId) {
          setLogins([]);
          setTotalRows(0);
          setError('Select a company to view employee logins.');
          return;
        }

        const params = new URLSearchParams();
        params.set('page', String(pageValue));
        params.set('limit', String(perPageValue));
        if (searchTerm.trim()) {
          params.set('search', searchTerm.trim());
        }
        if (statusFilter) {
          params.set('status', statusFilter);
        }

        const response = await apiRequest(
          `/api/admin/employee-logins?${params.toString()}`,
          {},
          navigate,
        );

        const items = Array.isArray(response.data?.items) ? response.data.items : [];
        const meta = response.data?.meta ?? {};

        setLogins(items);
        setTotalRows(Number(meta.total ?? items.length ?? 0));
        setPage(Number(meta.page ?? pageValue));
        setPerPage(Number(meta.pageSize ?? perPageValue));
      } catch (err) {
        setError(err.message || 'Failed to load employee login records.');
        setSuccessMessage('');
        setLogins([]);
      } finally {
        setLoading(false);
      }
    },
    [activeCompanyId, navigate, searchTerm, statusFilter],
  );

  useEffect(() => {
    fetchLogins(page, perPage);
  }, [fetchLogins, page, perPage]);

  useEffect(() => {
    const handleActiveCompanyChanged = (event) => {
      const nextCompanyId = event?.detail?.id ?? getActiveCompanyId();
      setActiveCompanyId(nextCompanyId);
      setPage(1);
      setSearchInput('');
      setSearchTerm('');
      setStatusFilter('');
    };

    window.addEventListener('activeCompanyChanged', handleActiveCompanyChanged);

    return () => {
      window.removeEventListener('activeCompanyChanged', handleActiveCompanyChanged);
    };
  }, []);

  const handleSearchSubmit = useCallback(
    (event) => {
      event.preventDefault();
      const trimmedValue = searchInput.trim();
      if (trimmedValue === searchTerm.trim()) {
        fetchLogins(1, perPage);
        setPage(1);
        return;
      }
      setSearchTerm(trimmedValue);
      setPage(1);
    },
    [fetchLogins, perPage, searchInput, searchTerm],
  );

  const handleResetFilters = useCallback(() => {
    setSearchInput('');
    setSearchTerm('');
    setStatusFilter('');
    setPage(1);
  }, []);

  const handleChangePage = useCallback(
    (pageValue) => {
      const nextPage = Number(pageValue);
      if (!Number.isNaN(nextPage)) {
        setPage(nextPage);
        fetchLogins(nextPage, perPage);
      }
    },
    [fetchLogins, perPage],
  );

  const handleChangeRowsPerPage = useCallback(
    (newPerPage, pageValue) => {
      const normalizedPerPage = Number(newPerPage) || DEFAULT_PAGE_SIZE;
      const normalizedPage = Number(pageValue) || 1;
      setPerPage(normalizedPerPage);
      setPage(normalizedPage);
      fetchLogins(normalizedPage, normalizedPerPage);
    },
    [fetchLogins],
  );

  const handleManageLogin = useCallback(
    (employeeId) => {
      if (employeeId) {
        navigate(`/employees/${employeeId}`);
      }
    },
    [navigate],
  );

  const handleOpenEditModal = useCallback((login) => {
    if (!login?.employee?.id) {
      return;
    }
    setEditingLogin(login);
    setEditUsername(login.username || '');
    setEditPassword('');
    setEditStatus((login.status || 'inactive').toLowerCase() === 'active' ? 'active' : 'disabled');
    setEditEducationMandatory(Boolean(login.educationMandatory ?? true));
    setEditError('');
    setIsEditModalOpen(true);
  }, []);

  const handleCloseEditModal = useCallback(() => {
    setIsEditModalOpen(false);
    setEditingLogin(null);
    setEditUsername('');
    setEditPassword('');
    setEditStatus('active');
    setEditEducationMandatory(true);
    setEditError('');
  }, []);

  const handleSubmitEdit = useCallback(
    async (event) => {
      event.preventDefault();
      if (!editingLogin?.employee?.id) {
        setEditError('Unable to determine which employee to update.');
        return;
      }

      const trimmedUsername = editUsername.trim();
      const trimmedPassword = editPassword.trim();

      if (!trimmedUsername) {
        setEditError('Username is required.');
        return;
      }

      if (trimmedPassword && trimmedPassword.length < 4) {
        setEditError('Password must be at least 4 characters long.');
        return;
      }

      setEditSaving(true);
      setEditError('');

      try {
        const payload = {
          username: trimmedUsername,
          status: editStatus,
          educationMandatory: editEducationMandatory,
        };

        if (trimmedPassword) {
          payload.password = trimmedPassword;
        }

        const response = await apiRequest(
          `/api/admin/employee-logins/${editingLogin.employee.id}`,
          {
            method: 'POST',
            body: payload,
          },
          navigate,
        );

        setSuccessMessage(response.message || 'Login credentials updated successfully.');
        setError('');
        handleCloseEditModal();
        fetchLogins(page, perPage);
      } catch (err) {
        setEditError(err.message || 'Failed to update login credentials.');
      } finally {
        setEditSaving(false);
      }
    },
    [editPassword, editStatus, editUsername, editingLogin, fetchLogins, navigate, page, perPage, handleCloseEditModal],
  );

  const columns = useMemo(
    () => [
      {
        name: 'Employee',
        sortable: true,
        grow: 2,
        selector: (row) => row?.employee?.name ?? '',
        cell: (row) => (
          <div className="employee-login-list__employee">
            <span className="employee-login-list__employee-name">
              {row?.employee?.name || '—'}
            </span>
            <span className="employee-login-list__employee-meta">
              {row?.employee?.temporaryId
                ? `Temporary ID: ${row.employee.temporaryId}`
                : 'Temporary ID: —'}
            </span>
            <span className="employee-login-list__employee-meta">
              {row?.employee?.permanentId
                ? `Permanent ID: ${row.employee.permanentId}`
                : 'Permanent ID: —'}
            </span>
            {(row?.employee?.outlet?.name || row?.employee?.outlet?.branchName) && (
              <span className="employee-login-list__employee-meta">
                Outlet: {row.employee.outlet.name || row.employee.outlet.branchName}
              </span>
            )}
          </div>
        ),
      },
      {
        name: 'Username',
        sortable: true,
        selector: (row) => row.username ?? '',
        cell: (row) => (
          <code className="employee-login-list__code">{row.username || '—'}</code>
        ),
      },
      {
        name: 'Shared Password',
        selector: (row) => row.temporaryPassword ?? '',
        cell: (row) => (
          <code className="employee-login-list__code">
            {row.temporaryPassword || '—'}
          </code>
        ),
      },
      {
        name: 'Status',
        selector: (row) => row.status ?? '',
        cell: (row) => (
          <span
            className={`employee-login-list__status employee-login-list__status--${
              (row.status || 'inactive').toLowerCase() === 'active' ? 'active' : 'disabled'
            }`}
          >
            {(row.status || 'inactive').toLowerCase() === 'active' ? 'Active' : 'Disabled'}
          </span>
        ),
      },
      {
        name: 'Education mandatory',
        selector: (row) => (row.educationMandatory === false ? 'No' : 'Yes'),
        cell: (row) => (
          <span className="employee-login-list__meta">
            {row.educationMandatory === false ? 'No (optional)' : 'Yes'}
          </span>
        ),
      },
      {
        name: 'Updated On',
        selector: (row) => row.updatedAt ?? '',
        cell: (row) => {
          if (!row.updatedAt) {
            return <span className="employee-login-list__meta">—</span>;
          }
          const date = new Date(row.updatedAt);
          if (Number.isNaN(date.getTime())) {
            return <span className="employee-login-list__meta">—</span>;
          }
          return (
            <span className="employee-login-list__meta">
              {date.toLocaleString(undefined, {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
              })}
            </span>
          );
        },
      },
      {
        name: 'Manage',
        cell: (row) => (
          <div className="employee-login-list__actions">
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              onClick={() => handleOpenEditModal(row)}
            >
              <FaEdit className="me-1" /> Edit
            </button>
            <button
              type="button"
              className="btn btn-outline-danger btn-sm"
              onClick={() => handleManageLogin(row?.employee?.id)}
            >
              <FaEye className="me-1" /> View
            </button>
          </div>
        ),
        ignoreRowClick: true,
        allowOverflow: true,
        button: true,
      },
    ],
    [handleManageLogin, handleOpenEditModal],
  );

  const toolbarContent = (
    <div className="row g-2 g-md-3 align-items-center">
      <div className="col-12 col-md-5 col-lg-4">
        <form className="employee-login-list__search" onSubmit={handleSearchSubmit}>
          <div className="input-group">
            <span className="input-group-text bg-white">
              <FaSearch aria-hidden="true" />
            </span>
            <input
              type="text"
              className="form-control"
              placeholder="Search by name, username or ID"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
            />
            <button type="submit" className="btn btn-danger">
              Search
            </button>
          </div>
        </form>
      </div>
      <div className="col-12 col-md-4 col-lg-3">
        <select
          className="form-select"
          value={statusFilter}
          onChange={(event) => {
            setStatusFilter(event.target.value);
            setPage(1);
          }}
        >
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="disabled">Disabled</option>
        </select>
      </div>
      <div className="col-12 col-md-3 col-lg-2">
        <button type="button" className="btn btn-outline-secondary w-100" onClick={handleResetFilters}>
          <FaSyncAlt className="me-1" /> Reset
        </button>
      </div>
    </div>
  );

  return (
    <DashboardLayout title="Employee Mobile Logins">
      <div className="employee-login-list">
        <div className="employee-login-list__header">
          <div>
            <h1>Employee Mobile Logins</h1>
            <p className="text-muted">
              Review and manage the usernames and shared passwords assigned to employees for the mobile HRMS
              application.
            </p>
          </div>
          <div className="employee-login-list__header-actions">
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => navigate('/dashboard/employee-logins/new')}
            >
              <FaUserPlus aria-hidden="true" className="me-2" />
              Create Login
            </button>
            <button type="button" className="btn btn-danger" onClick={() => navigate('/employees')}>
              <FaUsers aria-hidden="true" className="me-2" />
              Go to Employees
            </button>
          </div>
        </div>

        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}

        {successMessage && (
          <div className="alert alert-success" role="alert">
            {successMessage}
          </div>
        )}

        <CustomDataTable
          title="Mobile login credentials"
          columns={columns}
          data={logins}
          toolbarContent={toolbarContent}
          progressPending={loading}
          progressComponent={<div className="employee-login-list__loading">Loading logins…</div>}
          paginationServer
          paginationTotalRows={totalRows}
          paginationDefaultPage={page}
          paginationPerPage={perPage}
          onChangePage={handleChangePage}
          onChangeRowsPerPage={handleChangeRowsPerPage}
        />

        {isEditModalOpen && (
          <>
            <div className="modal fade show" style={{ display: 'block' }} role="dialog" aria-modal="true">
              <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">Edit Mobile Login</h5>
                    <button
                      type="button"
                      className="btn-close"
                      aria-label="Close"
                      onClick={handleCloseEditModal}
                      disabled={editSaving}
                    />
                  </div>
                  <form onSubmit={handleSubmitEdit}>
                    <div className="modal-body">
                      <p className="text-muted mb-3">
                        Update login credentials for{' '}
                        <strong>{editingLogin?.employee?.name || 'selected employee'}</strong>.
                      </p>
                      {editError && (
                        <div className="alert alert-danger" role="alert">
                          {editError}
                        </div>
                      )}
                      <div className="mb-3">
                        <label htmlFor="edit-username" className="form-label">
                          Username
                        </label>
                        <input
                          id="edit-username"
                          type="text"
                          className="form-control"
                          value={editUsername}
                          onChange={(event) => {
                            setEditUsername(event.target.value);
                            if (editError) {
                              setEditError('');
                            }
                          }}
                          disabled={editSaving}
                          required
                        />
                      </div>
                      <div className="mb-3">
                        <label htmlFor="edit-password" className="form-label">
                          New Password
                        </label>
                        <input
                          id="edit-password"
                          type="password"
                          className="form-control"
                          value={editPassword}
                          onChange={(event) => {
                            setEditPassword(event.target.value);
                            if (editError) {
                              setEditError('');
                            }
                          }}
                          disabled={editSaving}
                          placeholder="Leave blank to keep current password"
                        />
                        <div className="form-text">
                          Passwords must contain at least 4 characters.
                        </div>
                      </div>
                      <div className="mb-0">
                        <label htmlFor="edit-status" className="form-label">
                          Status
                        </label>
                        <select
                          id="edit-status"
                          className="form-select"
                          value={editStatus}
                          onChange={(event) => setEditStatus(event.target.value)}
                          disabled={editSaving}
                        >
                          <option value="active">Active</option>
                          <option value="disabled">Disabled</option>
                        </select>
                      </div>
                      <div className="mt-3">
                        <div className="form-check form-switch">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            role="switch"
                            id="edit-education-toggle"
                            checked={editEducationMandatory === true}
                            onChange={(event) => setEditEducationMandatory(event.target.checked)}
                            disabled={editSaving}
                          />
                          <label className="form-check-label" htmlFor="edit-education-toggle">
                            Education validation
                          </label>
                        </div>
                        <p className="text-muted small mb-0">
                          {editEducationMandatory
                            ? 'Educational Qualification will be mandatory during mobile registration.'
                            : 'Educational Qualification will be optional during mobile registration.'}
                        </p>
                      </div>
                    </div>
                    <div className="modal-footer">
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={handleCloseEditModal}
                        disabled={editSaving}
                      >
                        Cancel
                      </button>
                      <button type="submit" className="btn btn-danger" disabled={editSaving}>
                        {editSaving ? 'Saving…' : 'Save Changes'}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
            <div className="modal-backdrop fade show" />
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

export default EmployeeLoginList;
